// AuditTrailPanel — 內嵌「處置紀錄」(C6 混合制):Customer-360 / 裝置詳情頁內
// 直接列出該對象最近的 admin 處置軌跡(target_id 預填,零後端改動),case_id 為
// 可點 chip 深連 /audit?case_id=;完整查詢(操作者軸/時間窗)仍在獨立稽核頁。

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { formatTimestampUs, shortId } from '../../lib/format';
import { queryAuditLogs } from './auditApi';
import type { AuditLog } from './auditApi';
import { actionLabel } from './auditModel';

const PANEL_LIMIT = 15;

export function AuditTrailPanel({ targetId }: { targetId: string }) {
  const [logs, setLogs] = useState<AuditLog[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  // /users/:id ↔ /devices/:id 換頁不 unmount 本元件 — stale 回應必須被丟棄,
  // 否則 A 的處置紀錄可能蓋到 B 的頁面上。
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLogs(null);
    setError(null);
    try {
      const page = await queryAuditLogs({ axis: 'target_id', value: targetId, limit: PANEL_LIMIT });
      if (seq !== reqSeq.current) return;
      setLogs(page.logs);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e instanceof Error ? e : new Error('載入失敗'));
    }
  }, [targetId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="card">
      <div className="adm-panel-head">
        <h2 className="adm-card-title">處置紀錄</h2>
        <Link className="btn-ghost btn-sm" to={`/audit?target_id=${encodeURIComponent(targetId)}`}>
          完整稽核查詢
        </Link>
      </div>
      {logs === null && error == null && <LoadingState label="載入處置紀錄…" />}
      {error != null && <ErrorState error={error} onRetry={() => void load()} />}
      {logs !== null && logs.length === 0 && (
        <EmptyState icon="🗒️" title="尚無處置紀錄" detail="此對象沒有任何 admin 處置軌跡。" />
      )}
      {logs !== null && logs.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>時間</th>
                <th>動作</th>
                <th>操作者</th>
                <th>原因</th>
                <th>案件</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={`${l.ts_us}-${l.action}-${i}`}>
                  <td className="adm-mono adm-nowrap">{formatTimestampUs(l.ts_us)}</td>
                  <td className="adm-cell-strong adm-nowrap" title={l.action}>{actionLabel(l.action)}</td>
                  <td className="adm-mono text-tertiary adm-nowrap" title={l.actor_user_id}>
                    {l.actor_user_id ? shortId(l.actor_user_id) : '—'}
                  </td>
                  <td>{l.reason || '—'}</td>
                  <td className="adm-nowrap">
                    {l.case_id ? (
                      <Link className="pill pill-info chip-link" to={`/audit?case_id=${encodeURIComponent(l.case_id)}`}>
                        {l.case_id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
